import { useEffect, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '@dfq/shared/hooks/useStore';
import { fetchCategoryTree, selectCategoryTree } from '@dfq/shared/stores/search';
import { fetchNavigationSettings, selectNavigationSettings } from '@dfq/shared/stores/settingsSlice';

import { buildStorefrontChrome, type StorefrontChrome } from './adapters';

/**
 * Shared data source for the Storefront chrome (top nav + footer), wired to
 * `GET /api/v1/settings/navigation` and the live category tree. Used by
 * BOTH `PublicLayout` and `CustomerLayout` so there's a single fetch path
 * and a single filtering rule (SF-NAV-101) — the category tree is the same
 * `fetchCategoryTree`/`selectCategoryTree` pair `ProjectChooserContainer`
 * already uses, not a second endpoint.
 *
 * Both thunks carry a per-session condition guard (see `settingsSlice` /
 * `search` store), so mounting both layouts across navigations doesn't
 * duplicate the request.
 */
export function useStorefrontNavigation(): StorefrontChrome {
  const dispatch = useAppDispatch();
  const navigation = useAppSelector(selectNavigationSettings);
  const categoryTree = useAppSelector(selectCategoryTree);

  useEffect(() => {
    void dispatch(fetchNavigationSettings());
    void dispatch(fetchCategoryTree());
  }, [dispatch]);

  return useMemo(
    () => buildStorefrontChrome(navigation, categoryTree),
    [navigation, categoryTree],
  );
}
